import { useState } from "react";

import { useTradeStats } from "#/services/kyan/hooks/useTradeStats";
import { useCreateReferral } from "#/services/referrals/hooks/useCreateReferral";
import { useReferralsByAccount } from "#/services/referrals/hooks/useReferralsByAccount";
import { useAccount } from "#/store/wallet/account/account";
import { formatValue } from "#/utils/valueFormatters";

import {
  createReferralLinkItems,
  FIRST_REFERRAL_VOLUME,
  getReferralsAvailable,
  isEligibleForNewReferral,
  NEXT_REFERRAL_VOLUME,
} from "./ReferralLinks.helpers";

export const useReferralLinksController = () => {
  const [areLinksExpanded, setAreLinksExpanded] = useState(false);
  const { isAccountConnected } = useAccount();
  const { referrals, isLoading: isLoadingReferrals } = useReferralsByAccount();
  const { mutateAsync: createReferral, isPending: isPendingCreateReferral } =
    useCreateReferral();
  const { data: tradeStats, isLoading: isLoadingTradeStats } = useTradeStats();

  const totalAccountVolume =
    tradeStats?.groups?.[0]?.total_volume_notional ?? 0;
  const referralsCount = referrals?.length ?? 0;
  const isEligibleForNewRefCode = isEligibleForNewReferral(
    totalAccountVolume,
    referralsCount,
  );
  const referralsAvailable = getReferralsAvailable(
    totalAccountVolume,
    referralsCount,
  );
  const isLoading = isLoadingReferrals || isLoadingTradeStats;
  const shouldShowActionButton =
    (isEligibleForNewRefCode && !isLoading) || !isAccountConnected;
  const referralListItems = createReferralLinkItems(referrals);

  const referralVolumeAvailableText =
    isAccountConnected && referralsAvailable > 0 && !isLoading
      ? `You have unlocked ${referralsAvailable} referral codes based on your trade volume.`
      : "";
  const referralVolumeText =
    isAccountConnected &&
    !isLoading &&
    totalAccountVolume < FIRST_REFERRAL_VOLUME
      ? `Unlock your first Referral by trading ${formatValue(FIRST_REFERRAL_VOLUME, { template: "0 USDC" })} in notional volume.`
      : `Get 1 new referral for every ${formatValue(NEXT_REFERRAL_VOLUME, { template: "0 USDC" })} in traded notional volume. ${referralVolumeAvailableText}`;

  const handleToggleLinksExpanded = () => {
    setAreLinksExpanded((previous) => !previous);
  };

  return {
    isLoading,
    referralsCount,
    areLinksExpanded,
    referralListItems,
    isAccountConnected,
    referralVolumeText,
    shouldShowActionButton,
    isPendingCreateReferral,
    createReferral,
    handleToggleLinksExpanded,
  };
};
